import React, { useCallback, useEffect, useRef, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import i18n from 'i18n-react';

import c from './_fastTrackAnswerFeedback.module.scss';

const FEEDBACK_DURATION = 10;

export interface FastTrackAnswerFeedbackProps {
  variant: 'correct' | 'incorrect';
  message: React.ReactNode;
  onNext: () => void;
}

export function FastTrackAnswerFeedback({ variant, message, onNext }: FastTrackAnswerFeedbackProps) {
  const [secondsLeft, setSecondsLeft] = useState(FEEDBACK_DURATION);
  // Guards against onNext firing twice when the timer expiry races a button click.
  const firedRef = useRef(false);
  const onNextRef = useRef(onNext);

  useEffect(() => {
    onNextRef.current = onNext;
  }, [onNext]);

  const handleNext = useCallback(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    onNextRef.current();
  }, []);

  useEffect(() => {
    if (secondsLeft <= 0) {
      handleNext();
      return undefined;
    }
    const id = setTimeout(() => {
      setSecondsLeft((s) => {
        return s - 1;
      });
    }, 1000);
    return () => {
      clearTimeout(id);
    };
  }, [secondsLeft, handleNext]);

  const isCorrect = variant === 'correct';

  return (
    <div
      className={clsx('fast-track-feedback', c.wrapper, {
        [c.fastTrackFeedbackCorrect]: isCorrect,
        [c.fastTrackFeedbackIncorrect]: !isCorrect,
      })}
      role="status"
    >
      <p className={c.fastTrackFeedbackMessage}>
        <span className={c.fastTrackFeedbackIcon}>
          <FontAwesomeIcon icon={['fas', isCorrect ? 'check' : 'xmark']} />
        </span>
        {message}
      </p>
      <div className={c.fastTrackFeedbackTimer}>
        <div className={c.fastTrackFeedbackTimerTrack}>
          <div className={c.fastTrackFeedbackTimerFill} style={{ animationDuration: `${FEEDBACK_DURATION}s` }} />
        </div>
        <span className={c.fastTrackFeedbackTimerLabel}>
          {i18n.translate('FastTrackView.nextIn', { count: Math.max(secondsLeft, 0) })}
        </span>
      </div>
      <button type="button" className={c.fastTrackFeedbackNextBtn} onClick={handleNext}>
        {i18n.translate('FastTrackView.goToNextQuestion')}
        <FontAwesomeIcon icon={['fas', 'arrow-right']} />
      </button>
    </div>
  );
}
